import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { AlertOctagon, AlertTriangle, Calculator, History, StickyNote, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import PedsDoseCalculator, { type AppliedDose } from "@/components/PedsCalculator/PedsDoseCalculator";
import { usePermission } from "@/hooks/usePermission";
import { usePrescriptionDoseChecks } from "@/hooks/usePrescriptionDoseChecks";
import { useDrugInteractions } from "@/hooks/useDrugInteractions";
import type { DrugInteraction } from "@/lib/api";
import { parseDosagePattern, parseDurationDays } from "@/lib/dosage";
import { parseAllergies, checkAllergies } from "@/lib/allergyCheck";
import AutocompleteInput from "@/components/shared/AutocompleteInput";
import DrugSearchInput from "@/components/shared/DrugSearchInput";
import CompositionEditor from "@/components/shared/CompositionEditor";
import { RxTemplatesContext, TemplateMenu, getPreview } from "@/components/Template/RxTemplates";
import { useAuth } from "@/context/AuthContext";
import { DrugSearchResult, recordDrugUsage, searchDrugs, updateCustomDrug } from "@/lib/api";
import { toast } from "@/components/ui/use-toast";
import {
    getDosageOptions,
    WHEN_OPTIONS,
    DURATION_OPTIONS,
    Drug,
} from "@/lib/constants";
import { Patient } from "@/types";
import SectionPropertiesProps from "@/components/shared/SectionProperties.tsx";
import { SafetyAlert } from "@/components/ui/safety-alert";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { medicinesToRepeat, nameKey, repeatLastMedicines, sameName } from "@/lib/repeatMedicines";
import { formatDate } from "@/lib/formatDate";


// Per-row shape stored in RHF. Includes drug meta needed for dosage options.
const DEFAULT_ROW = {
    drug_id:      null as number | null,
    product:      "",
    brand_name:   "",
    generic_name: "",
    // Drug meta retained for smart dosage options — not submitted to API
    _drug_form:   "",
    _drug_brand:  "",
    // Prescription fields
    dosage:       "",
    taken_when:   "",
    route:        "",
    duration:     "",
    quantity:     "",
    notes:        "",
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function dobToAgeMonths(dob: string | undefined): number | null {
    if (!dob) return null;
    const birth = new Date(dob);
    if (isNaN(birth.getTime())) return null;
    const now = new Date();
    const months = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
    return now.getDate() >= birth.getDate() ? months : months - 1;
}

const SEVERITY_ORDER: Record<DrugInteraction["severity"], number> = { contraindicated: 0, severe: 1, moderate: 2, mild: 3 };
const SEVERITY_STYLE: Record<DrugInteraction["severity"], string> = {
    contraindicated: "bg-destructive text-destructive-foreground",
    severe:          "bg-destructive/10 text-destructive",
    moderate:        "bg-warning/15 text-warning",
    mild:            "bg-muted text-muted-foreground",
};

// Mockup row: # · Medicine · Dose · When · Duration · Qty · remove. Only the medicine name grows; the others
// are fixed at what their everyday values need (14px text + the cell's 8px padding):
//   Dose      124px — "1-0-1" most of the time, "5 ml - 0 - 5 ml" beside the calculator icon, "10 ml - 0 - 10 ml" (106px) without it
//   When      108px — "After Food" … "Empty Stomach" (100px); rarer longer ones scroll, full text on hover
//   Duration  84px  — "As directed" (72px)
//   Qty       44px  — three digits
//   Medicine  the rest — long brand names wrap to a second line rather than being cut
const MED_GRID = "grid grid-cols-[24px_minmax(0,1fr)_124px_108px_84px_44px_28px] gap-x-2 px-3";
// Narrower than this, each medicine takes two lines (phones); wider, one row with at least ~160px for the
// medicine name (iPad mini portrait and up). Decided by the table's own width, not the screen's.
const STACKED_BELOW_PX = 640;

const CHECK_TONE = {
    ok:      "text-success",
    warning: "text-warning",
    error:   "text-destructive font-medium",
    none:    "text-muted-foreground",
} as const;


// Catalog lookups for relinking typed medicines, shared across rows and renders. Relinks only
// when the name matches one catalog drug (or several with the same composition).
const relinkCache = new Map<string, Promise<DrugSearchResult | null>>();
function findCatalogDrug(name: string): Promise<DrugSearchResult | null> {
    const key = nameKey(name);
    if (!relinkCache.has(key)) {
        relinkCache.set(key, searchDrugs(name)
            .then(rows => {
                const hits = rows.filter(d => sameName(d.brand_name, name));
                const compositions = new Set(hits.map(d => (d.composition_raw ?? "").toLowerCase()));
                return hits.length > 0 && compositions.size === 1 ? hits[0] : null;
            })
            .catch(() => { relinkCache.delete(key); return null; }));
    }
    return relinkCache.get(key)!;
}

function rowToDrug(form: string, brand: string): Drug {
    return {
        form:       form  || "tablet",
        brand_name: brand || "",
    };
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function MedicineListForm({ patient, interactionAlertsRef, lastPrescription }: SectionPropertiesProps) {
    const {
        control,
        register,
        getValues,
        setValue,
        watch,
        trigger,
        formState: { errors },
    } = useFormContext();

    const { fields, append, remove, update } = useFieldArray({ control, name: "medicines" });
    const tableRef = useRef<HTMLDivElement>(null);
    const [stacked, setStacked] = useState(false);
    useLayoutEffect(() => {
        const el = tableRef.current;
        if (!el) return;
        const measure = () => setStacked(el.getBoundingClientRect().width < STACKED_BELOW_PX);
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const watched = watch("medicines") || [];
    const repeatable = medicinesToRepeat(lastPrescription, watched);
    const [initialized, setInitialized] = useState(false);
    const { currentUser } = useAuth();
    const { hasFeature } = usePermission();
    const [calcRowIndex, setCalcRowIndex] = useState<number | null>(null);
    // Medicines on the pad that the clinic added itself — a composition typed here is saved to them
    const [ownDrugIds, setOwnDrugIds] = useState<number[]>([]);
    const rememberOwn = (drug: DrugSearchResult) => {
        if (drug.is_own) setOwnDrugIds(ids => (ids.includes(drug.id) ? ids : [...ids, drug.id]));
    };

    // Paediatric flag — use age if available, else derive from date_of_birth
    const isPaediatric = (() => {
        if (patient?.age !== undefined) return patient.age < 18;
        if (patient?.date_of_birth) {
            const birth = new Date(patient.date_of_birth);
            if (!isNaN(birth.getTime())) {
                const ageYears =
                    (new Date().getFullYear() - birth.getFullYear());
                return ageYears < 18;
            }
        }
        return false;
    })();

    // Patient context for dose checks — DOB is more precise than a stored age in years
    const ageMonths = dobToAgeMonths(patient?.date_of_birth) ?? (patient?.age !== undefined ? patient.age * 12 : null);
    const weightKg = parseFloat(watch("vitals.weight") ?? "");
    // The typed-dose check is free on every plan (safety); the calculator follows the plan
    const doseChecks = usePrescriptionDoseChecks(watched, weightKg, ageMonths, isPaediatric);
    const canUseCalculator = isPaediatric && hasFeature("peds_calculator");
    // Allergy check runs for every patient and plan
    const allergyTerms = parseAllergies(patient?.allergies);

    // Relink medicines that name a catalog drug but carry no link: typed without picking from the
    // list, edited back to the same name, or loaded from an older prescription. Without the link the
    // interaction check skips the line. Only catalog fields are filled; dose and timing are untouched.
    const unlinkedKey = watched
        .map((m: any, i: number) => (!m?.drug_id && (m?.product ?? "").trim().length >= 3 ? `${i}:${m.product.trim()}` : ""))
        .filter(Boolean).join("|");
    useEffect(() => {
        if (!unlinkedKey) return;
        let cancelled = false;
        const timer = setTimeout(() => {
            unlinkedKey.split("|").forEach(entry => {
                const sep = entry.indexOf(":");
                const index = Number(entry.slice(0, sep));
                const name = entry.slice(sep + 1);
                findCatalogDrug(name).then(drug => {
                    // The row may have changed while the lookup ran
                    if (cancelled || !drug || getValues(`medicines.${index}.drug_id`)) return;
                    if (!sameName(getValues(`medicines.${index}.product`), drug.brand_name)) return;
                    const quiet = { shouldDirty: false, shouldTouch: false, shouldValidate: false };
                    setValue(`medicines.${index}.drug_id`,     drug.id,               quiet);
                    setValue(`medicines.${index}._drug_brand`, drug.brand_name ?? "", quiet);
                    rememberOwn(drug);
                    if (!getValues(`medicines.${index}.generic_name`)) setValue(`medicines.${index}.generic_name`, drug.composition_raw ?? "", quiet);
                    if (!getValues(`medicines.${index}._drug_form`))   setValue(`medicines.${index}._drug_form`,   drug.form ?? "",            quiet);
                    if (!getValues(`medicines.${index}.route`))        setValue(`medicines.${index}.route`,        drug.route ?? "",           quiet);
                });
            });
        }, 600);
        return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unlinkedKey]);

    // Drug–drug interactions between catalog medicines (plan feature)
    const catalogDrugIds = watched.map((m: any) => m?.drug_id).filter((id: unknown): id is number => typeof id === "number");
    const { interactions, failed: interactionsFailed } = useDrugInteractions(catalogDrugIds, hasFeature("drug_interactions"));
    // Which prescription lines contain an ingredient, for "Ecosprin + Warf 5" style labels. The server
    // names the drugs behind each side (salts count as their ingredient: ferrous ascorbate → iron);
    // the name match is the fallback.
    const linesWith = (moleculeName: string, drugIds?: number[]) => {
        const needle = moleculeName.toLowerCase();
        const names = watched
            .filter((m: any) => drugIds?.length
                ? drugIds.includes(m?.drug_id)
                : `${m?.generic_name ?? ""} ${m?.product ?? ""}`.toLowerCase().includes(needle))
            .map((m: any) => m?.product || m?.brand_name)
            .filter(Boolean);
        return names.length ? `${moleculeName} (${[...new Set(names)].join(", ")})` : moleculeName;
    };
    // A line's interactions, named by the other lines involved ("Interacts with Ferrous Max Tablet")
    const interactionsFor = (drugId: unknown) => typeof drugId !== "number" ? [] : interactions.flatMap(i => {
        const side = i.drug_ids_1?.includes(drugId) ? i.drug_ids_2 : i.drug_ids_2?.includes(drugId) ? i.drug_ids_1 : null;
        const others = [...new Set(watched.filter((o: any) => side?.includes(o?.drug_id) && o?.drug_id !== drugId).map((o: any) => o?.product).filter(Boolean))];
        return others.length ? [{ key: String(i.id), severity: i.severity, others: others.join(", "), duplicate: !!i.duplicate }] : [];
    });
    const interactionLine = (i: DrugInteraction) => i.duplicate
        ? `Duplicate ${linesWith(i.molecule_1_name, i.drug_ids_1)}`
        : `${linesWith(i.molecule_1_name, i.drug_ids_1)} + ${linesWith(i.molecule_2_name, i.drug_ids_2)}`;
    const blockingInteractions = interactions
        .filter(i => i.severity === "severe" || i.severity === "contraindicated")
        .map(i => `• ${interactionLine(i)}: ${i.severity} — ${i.description}`);
    const blockingKey = blockingInteractions.join("|");
    // Handed to the save step (usePrescriptionForm), which asks for confirmation. A ref, not a form
    // field: an extra field makes the form dirty on open and breaks the "has content" check behind Save.
    useEffect(() => {
        if (interactionAlertsRef) interactionAlertsRef.current = blockingInteractions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blockingKey, interactionAlertsRef]);

    // Initialize with one empty row if none exist
    useEffect(() => {
        if (!initialized && fields.length === 0) {
            append(DEFAULT_ROW);
            setInitialized(true);
        }
    }, [fields.length, append, initialized]);

    // ── Drug selection ──────────────────────────────────────────────────────────

    const handleDrugSelect = (index: number, drug: DrugSearchResult) => {
        rememberOwn(drug);
        setValue(`medicines.${index}.drug_id`,      drug.id,                     { shouldDirty: false });
        setValue(`medicines.${index}.brand_name`,   drug.brand_name,             { shouldDirty: true });
        setValue(`medicines.${index}.product`,      drug.brand_name,             { shouldDirty: true });
        setValue(`medicines.${index}.generic_name`, drug.composition_raw ?? "",  { shouldDirty: false });

        // Retain drug meta for dosage option resolution (not submitted to API)
        setValue(`medicines.${index}._drug_form`,  drug.form ?? "tablet", { shouldDirty: false });
        setValue(`medicines.${index}._drug_brand`, drug.brand_name ?? "",  { shouldDirty: false });

        // Auto-fill route from drug catalog (e.g. oral, topical, injection)
        setValue(`medicines.${index}.route`, drug.route ?? "oral", { shouldDirty: false });

        const dosage    = drug.custom_dosage     ?? drug.default_dosage;
        const takenWhen = drug.custom_taken_when ?? drug.default_taken_when;
        const duration  = drug.custom_duration   ?? drug.default_duration;
        const notes     = drug.custom_notes      ?? drug.default_notes;
        if (dosage)    setValue(`medicines.${index}.dosage`,     dosage,    { shouldDirty: false });
        if (takenWhen) setValue(`medicines.${index}.taken_when`, takenWhen, { shouldDirty: false });
        if (duration)  setValue(`medicines.${index}.duration`,   duration,  { shouldDirty: false });
        if (notes)     setValue(`medicines.${index}.notes`,      notes,     { shouldDirty: false });
        // Quantity from the auto-filled dose × duration (tablets/capsules only);
        // cleared first so a previous medicine's quantity never carries over
        setValue(`medicines.${index}.quantity`, "", { shouldDirty: false });
        if (dosage || duration) handleAutoQuantity(index);

        if (currentUser?.doctor_id && currentUser?.tenant_id) {
            recordDrugUsage(drug.id, currentUser.doctor_id, currentUser.tenant_id).catch(() => {});
        }
    };

    // ── Dosage options for a row ────────────────────────────────────────────────

    const getRowDosageOptions = (index: number): string[] => {
        const form  = getValues(`medicines.${index}._drug_form`)  || "";
        const brand = getValues(`medicines.${index}._drug_brand`) || "";
        return getDosageOptions(rowToDrug(form, brand), isPaediatric);
    };

    // ── Auto-quantity calculation ───────────────────────────────────────────────

    const handleAutoQuantity = (index: number) => {
        setTimeout(() => {
            // Only auto-calculate for tablets and capsules.
            // Syrups → bottles, Injections → amps/vials, Inhalers → units.
            // These are not calculable from dosage×duration — doctor fills manually.
            const drugForm = (getValues(`medicines.${index}._drug_form`) || "tablet").toLowerCase();
            const calculableForms = ["tablet", "capsule"];
            if (!calculableForms.includes(drugForm)) return;

            const parsed       = parseDosagePattern(getValues(`medicines.${index}.dosage`));
            const durationDays = parseDurationDays(getValues(`medicines.${index}.duration`));
            const dailyDoses   = parsed && parsed.unit === "unit" ? parsed.perDay : 0;

            if (dailyDoses > 0 && durationDays > 0) {
                const total = Math.ceil(dailyDoses * durationDays);
                setValue(`medicines.${index}.quantity`, total.toString(), {
                    shouldValidate: true,
                    shouldDirty:    true,
                    shouldTouch:    true,
                });
                trigger(`medicines.${index}.quantity`);
            }
        }, 100);
    };

    // ── Focus helpers ───────────────────────────────────────────────────────────

    const focusNextField = (index: number, fieldOffset: number) => {
        const nextIndex = index * 7 + fieldOffset;
        if (inputRefs.current[nextIndex]) {
            setTimeout(() => inputRefs.current[nextIndex]?.focus(), 50);
        }
    };

    // ── Rows ────────────────────────────────────────────────────────────────────
    // As in the mockup: one row per prescribed medicine, new ones typed into the box below the list.
    // A row with nothing in it (the form's starting row) isn't shown; the box fills it first.

    const hasContent = (m: any) =>
        ["product", "dosage", "taken_when", "duration", "quantity", "notes"].some(k => String(m?.[k] ?? "").trim() !== "");

    const visibleRows = fields.map((_, index) => ({ index })).filter(({ index }) => hasContent(watched[index]));

    const addInputRef   = useRef<HTMLInputElement | null>(null);
    const addTextRef    = useRef("");
    const justPickedRef = useRef(false);
    const [addKey, setAddKey] = useState(0);            // remounts the box to clear it
    const [noteOpenId, setNoteOpenId] = useState<string | null>(null);   // row whose note box is open
    const [compositionOpenId, setCompositionOpenId] = useState<string | null>(null);
    const [compositionDraft, setCompositionDraft] = useState("");
    const compositionCancelled = useRef(false);   // Escape closes the box without saving

    // A medicine typed in without a composition: fill the generic for this prescription (and the
    // allergy check), and keep it on the clinic's own medicine for next time
    const saveComposition = (index: number) => {
        const value = compositionCancelled.current ? "" : compositionDraft.trim();
        setCompositionOpenId(null);
        if (!value) return;
        setValue(`medicines.${index}.generic_name`, value, { shouldDirty: true });
        const drugId = getValues(`medicines.${index}.drug_id`);
        if (typeof drugId === "number" && ownDrugIds.includes(drugId)) {
            updateCustomDrug(drugId, { composition_raw: value })
                .catch(() => toast({ title: "Couldn't save the composition for next time", variant: "destructive" }));
        }
    };

    const rowForNewMedicine = (): number => {
        const empty = (getValues("medicines") ?? []).findIndex((m: any) => !hasContent(m));
        if (empty !== -1) return empty;
        append(DEFAULT_ROW, { shouldFocus: false });
        return fields.length;
    };

    const focusDose = (index: number) => setTimeout(() => inputRefs.current[index * 7 + 1]?.focus(), 150);

    // Medicine templates: offered in the add box when their name is typed, and behind "Templates"
    const rxTemplates = useContext(RxTemplatesContext);
    const templateOptions = useMemo(
        () => rxTemplates?.templates.filter(t => t.type === "medicine").map(t => ({ id: t.id, title: t.title, hint: getPreview(t) })),
        [rxTemplates?.templates],
    );

    const addPicked = (drug: DrugSearchResult) => {
        justPickedRef.current = true;
        const index = rowForNewMedicine();
        handleDrugSelect(index, drug);
        addTextRef.current = "";
        setAddKey(k => k + 1);
        focusDose(index);
        setTimeout(() => { justPickedRef.current = false; }, 200);
    };

    // Enter on a name that isn't in the catalog: a one-off medicine (the relink above still tries the catalog)
    const addTyped = () => {
        if (justPickedRef.current) return;
        const name = addTextRef.current.trim();
        if (!name) return;
        const index = rowForNewMedicine();
        setValue(`medicines.${index}.product`, name, { shouldDirty: true });
        addTextRef.current = "";
        setAddKey(k => k + 1);
        focusDose(index);
    };

    const removeRow = (index: number) => {
        if (fields.length > 1) remove(index);
        else update(index, { ...DEFAULT_ROW });
    };

    const focusAdd = () => setTimeout(() => addInputRef.current?.focus(), 50);

    // Fields read as text until hovered or focused, like the mockup's rows (32px, the name 28px so its
    // generic fits under it). Touch screens can't hover, so there only the fields still to fill keep an
    // outline (filled ones read as text, as on desktop). The stacked (phone) layout always outlines them.
    const cellClass = (stacked: boolean, filled: boolean, invalid?: unknown, tall?: boolean) => cn(
        "flex items-center min-w-0 rounded-md border px-1 transition-colors",
        "focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/20",
        // The name cell grows (min-height) when a long brand name wraps to a second line
        stacked ? cn(tall ? "min-h-9 touch:min-h-10" : "h-9 touch:h-10", "px-2 border-field bg-card")
                : cn(tall ? "min-h-5 touch:min-h-10" : "h-8 touch:h-10", "border-transparent hover:border-field", !filled && "touch:border-field"),
        !!invalid && "border-destructive/50",
    );
    const inputText = "text-sm py-0 px-0";

    const renderRow = (index: number, n: number, stacked: boolean) => {
        const m = watched[index] ?? {};
        const fieldId = fields[index].id;
        const generic = m.generic_name ?? "";
        const allergyHits = checkAllergies(allergyTerms, { composition: generic, product: m.product ?? "" });
        const rowInteractions = interactionsFor(m.drug_id);
        // The same medicine on another line: usually added twice by mistake, sometimes a split dose
        const sameOnLines = visibleRows.flatMap(({ index: j }, i) => j !== index && sameName(watched[j]?.product, m.product) ? [i + 1] : []);
        const check = doseChecks.get(index);
        const options = getRowDosageOptions(index);
        const rowErrors = errors.medicines?.[index];
        const note = String(m.notes ?? "").trim();
        const hasProduct = !!String(m.product ?? "").trim();
        const rawQuantity = m.quantity;
        const quantityValue = !hasProduct || rawQuantity == null || rawQuantity === "" || rawQuantity === "0" || rawQuantity === 0 ? "" : String(rawQuantity);
        const label = (text: string) => stacked ? <span className="block mb-1 text-xs text-muted-foreground">{text}</span> : null;

        const medicine = (
            <div className="min-w-0">
                <div className={cellClass(stacked, hasProduct, rowErrors?.product, true)}>
                    {/* Long brand names ("A-Set Oral Suspension Mango Sugar Free") wrap instead of being cut:
                        at rest the name shows as text, with the search field on top of it, invisible until focused */}
                    <div className="group/name relative flex-1 min-w-0">
                    {hasProduct && (
                        <p aria-hidden className="text-sm font-medium leading-5 break-words line-clamp-2 pointer-events-none group-focus-within/name:invisible">
                            {m.product}
                        </p>
                    )}
                    <div
                        className={cn(hasProduct && "absolute inset-0 flex items-center opacity-0 group-focus-within/name:opacity-100")}
                        // A tap anywhere on the (wrapped) name edits it, not only on the one-line field
                        onMouseDown={(e) => {
                            if ((e.target as HTMLElement).tagName === "INPUT") return;
                            e.preventDefault();
                            inputRefs.current[index * 7]?.focus();
                        }}
                    >
                    <div className="w-full">
                    <DrugSearchInput
                        value={getValues(`medicines.${index}.product`) ?? ""}
                        onChange={(drug) => handleDrugSelect(index, drug)}
                        onCustomInput={(val) => {
                            setValue(`medicines.${index}.product`,      val,  { shouldDirty: false, shouldTouch: false, shouldValidate: false });
                            // Still the linked drug (case/spacing edits): keep its catalog link and checks
                            const linkedBrand = getValues(`medicines.${index}._drug_brand`) || getValues(`medicines.${index}.brand_name`);
                            if (getValues(`medicines.${index}.drug_id`) && sameName(val, linkedBrand)) return;
                            setValue(`medicines.${index}.drug_id`,      null, { shouldDirty: false, shouldTouch: false, shouldValidate: false });
                            setValue(`medicines.${index}._drug_form`,   "",   { shouldDirty: false });
                            setValue(`medicines.${index}._drug_brand`,  "",   { shouldDirty: false });
                            setValue(`medicines.${index}.generic_name`, "",   { shouldDirty: false });
                            setValue(`medicines.${index}.route`,        "",   { shouldDirty: false });
                        }}
                        placeholder="Medicine"
                        onEnterNext={() => focusNextField(index, 1)}
                        className={cn(inputText, "font-medium")}
                        ref={(el) => { inputRefs.current[index * 7] = el; }}
                    />
                    </div>
                    </div>
                    </div>
                    {/* The note prints under the medicine; here it is an icon, and the text after the generic */}
                    <Popover open={noteOpenId === fieldId} onOpenChange={(o) => setNoteOpenId(o ? fieldId : null)}>
                        <PopoverTrigger asChild>
                            <button
                                type="button"
                                aria-label={note ? `Edit note: ${note}` : "Add a note"}
                                title={note ? `Note: ${note}` : "Add a note (prints on the prescription)"}
                                className={cn("touch-target shrink-0 p-0.5 rounded hover:bg-accent", note ? "text-primary" : "text-muted-foreground/50 hover:text-muted-foreground")}
                            >
                                <StickyNote className="w-3.5 h-3.5" />
                            </button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-72 p-3 space-y-2">
                            <label className="block text-[13px] font-medium text-foreground" htmlFor={`note-${fieldId}`}>
                                Note for {m.product || "this medicine"}
                            </label>
                            <Input
                                id={`note-${fieldId}`}
                                autoFocus
                                value={m.notes ?? ""}
                                onChange={(e) => setValue(`medicines.${index}.notes`, e.target.value, { shouldDirty: true })}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setNoteOpenId(null); focusAdd(); } }}
                                placeholder="e.g. 5 ml each time"
                                className="h-9 text-sm"
                            />
                            <p className="text-xs text-muted-foreground">Prints under the medicine on the prescription.</p>
                        </PopoverContent>
                    </Popover>
                </div>
                {hasProduct && !generic && (
                    <Popover open={compositionOpenId === fieldId} onOpenChange={(o) => {
                        if (!o) return saveComposition(index);
                        setCompositionDraft("");
                        compositionCancelled.current = false;
                        setCompositionOpenId(fieldId);
                    }}>
                        <PopoverTrigger asChild>
                            <button type="button" className={cn("block text-xs leading-4 text-muted-foreground hover:text-foreground hover:underline", stacked ? "px-2 pt-0.5" : "px-1")}>
                                + Add composition
                            </button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-[22rem] max-w-[calc(100vw-2rem)] p-3 space-y-2" onEscapeKeyDown={() => { compositionCancelled.current = true; }}>
                            <label className="block text-[13px] font-medium text-foreground" htmlFor={`composition-${fieldId}`}>
                                Composition of {m.product}
                            </label>
                            <CompositionEditor
                                id={`composition-${fieldId}`}
                                value=""
                                onChange={setCompositionDraft}
                                onDone={() => saveComposition(index)}
                                autoFocus
                            />
                            <div className="flex items-center justify-between gap-3 pt-1">
                                <p className="text-xs text-muted-foreground">
                                    {typeof m.drug_id === "number" && ownDrugIds.includes(m.drug_id)
                                        ? "Also saved to this medicine for next time."
                                        : "For this prescription only."}
                                </p>
                                <Button type="button" size="sm" className="shrink-0" onClick={() => saveComposition(index)}>Done</Button>
                            </div>
                        </PopoverContent>
                    </Popover>
                )}
                {(generic || note) && (
                    <p className={cn("text-xs leading-4 text-muted-foreground truncate", stacked ? "px-2 pt-0.5" : "px-1")} title={[generic, note && `Note: ${note}`].filter(Boolean).join(" · ")}>
                        {generic}
                        {generic && note && " · "}
                        {note && <span className="text-foreground/75">Note: {note}</span>}
                    </p>
                )}
                {allergyHits.map(hit => (
                    <p key={hit.text} role="alert"
                        className={cn("flex items-start gap-1 pt-0.5 text-xs leading-snug", stacked ? "px-2" : "px-1",
                            hit.level === "error" ? "text-destructive font-medium" : "text-warning")}>
                        {hit.level === "error"
                            ? <AlertOctagon className="w-3.5 h-3.5 mt-px shrink-0" />
                            : <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />}
                        {hit.text}
                    </p>
                ))}
                {sameOnLines.length > 0 && (
                    <p role="alert" className={cn("flex items-start gap-1 pt-0.5 text-xs leading-snug text-warning", stacked ? "px-2" : "px-1")}>
                        <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
                        <span>Also on line {sameOnLines.join(", ")} — remove one unless it's a split dose</span>
                    </p>
                )}
                {/* Also on the line itself: the list under the table is easy to miss with the iPad keyboard up */}
                {rowInteractions.map(x => (
                    <p key={x.key}
                        className={cn("flex items-start gap-1 pt-0.5 text-xs leading-snug", stacked ? "px-2" : "px-1",
                            x.severity === "severe" || x.severity === "contraindicated" ? "text-destructive font-medium"
                                : x.severity === "moderate" ? "text-warning" : "text-muted-foreground")}>
                        <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
                        <span>{x.duplicate ? "Same ingredient as" : "Interacts with"} {x.others} <span className="capitalize">({x.severity})</span></span>
                    </p>
                ))}
            </div>
        );

        const dose = (
            <div className="min-w-0">
                {label("Dose")}
                <div className={cn(cellClass(stacked, !!String(m.dosage ?? "").trim(), rowErrors?.dosage), "pr-1")}
                    title={check && check.level !== "none" ? (check.notes.map(n => n.text).join(" · ") || check.summary) : (m.dosage || undefined)}>
                    <AutocompleteInput
                        value={getValues(`medicines.${index}.dosage`) ?? ""}
                        onChange={(val) => {
                            setValue(`medicines.${index}.dosage`, val, { shouldDirty: true });
                            handleAutoQuantity(index);
                        }}
                        options={options}
                        placeholder={options[0] ?? "Dose"}
                        onEnterNext={() => focusNextField(index, 2)}
                        className={cn(inputText, "tabular-nums")}
                        containerClassName="px-0 flex-1 min-w-0"
                        showAllOnFocus={true}
                        dropdownMinWidth={160}
                        ref={(el) => { inputRefs.current[index * 7 + 1] = el; }}
                    />
                    {canUseCalculator && (
                        <button
                            type="button"
                            onClick={() => setCalcRowIndex(index)}
                            className="touch-target shrink-0 p-1 rounded text-muted-foreground hover:text-primary hover:bg-primary/10"
                            title="Calculate dose by weight"
                            aria-label="Calculate dose by weight"
                        >
                            <Calculator className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
                {/* A line only when there's something to act on; a passing check is the field's tooltip */}
                {check && (check.level === "warning" || check.level === "error") && (
                    <p
                        className={cn("pt-0.5 text-xs leading-tight truncate", stacked ? "px-2" : "px-1", CHECK_TONE[check.level])}
                        title={check.notes.map(n => n.text).join("\n") || check.summary}
                    >
                        {check.summary}
                    </p>
                )}
            </div>
        );

        const when = (
            <div className="min-w-0">
                {label("When")}
                <div className={cellClass(stacked, !!String(m.taken_when ?? "").trim(), rowErrors?.taken_when)} title={m.taken_when || undefined}>
                    <AutocompleteInput
                        value={getValues(`medicines.${index}.taken_when`) ?? ""}
                        onChange={(val) => setValue(`medicines.${index}.taken_when`, val, { shouldDirty: true })}
                        options={WHEN_OPTIONS}
                        placeholder="When"
                        onEnterNext={() => focusNextField(index, 4)}
                        className={cn(inputText, "text-label")}
                        containerClassName="px-0 flex-1 min-w-0"
                        showAllOnFocus={true}
                        dropdownMinWidth={200}
                        ref={(el) => { inputRefs.current[index * 7 + 2] = el; }}
                    />
                </div>
            </div>
        );

        const duration = (
            <div className="min-w-0">
                {label("Duration")}
                <div className={cellClass(stacked, !!String(m.duration ?? "").trim(), rowErrors?.duration)}>
                    <AutocompleteInput
                        value={getValues(`medicines.${index}.duration`) ?? ""}
                        onChange={(val) => {
                            setValue(`medicines.${index}.duration`, val, { shouldDirty: true });
                            handleAutoQuantity(index);
                        }}
                        options={DURATION_OPTIONS}
                        placeholder="5 days"
                        onEnterNext={() => focusNextField(index, 5)}
                        className={cn(inputText, "text-label")}
                        containerClassName="px-0 flex-1 min-w-0"
                        showAllOnFocus={true}
                        dropdownMinWidth={200}
                        ref={(el) => { inputRefs.current[index * 7 + 4] = el; }}
                    />
                </div>
            </div>
        );

        const quantity = (
            <div className="min-w-0">
                {label("Qty")}
                <div className={cellClass(stacked, quantityValue !== "", rowErrors?.quantity)}>
                    <Input
                        type="number"
                        {...register(`medicines.${index}.quantity`)}
                        value={quantityValue}
                        min="0"
                        onChange={(e) => {
                            const value = e.target.value;
                            // Whole units only: the quantity is saved as a whole number, so 1.5 would quietly become 2
                            if (/^\d{0,4}$/.test(value)) {
                                setValue(`medicines.${index}.quantity`, value, { shouldValidate: true, shouldDirty: true });
                            }
                        }}
                        aria-label="Quantity"
                        className={cn(
                            "h-auto w-full p-0 text-sm font-mono text-right bg-transparent shadow-none border-0 outline-none ring-0 focus-visible:ring-0 focus-visible:ring-offset-0",
                            "placeholder:text-muted-foreground/40",
                            "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                        )}
                        placeholder=""
                        enterKeyHint="next"
                        ref={(el) => { inputRefs.current[index * 7 + 5] = el; }}
                        onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "e" || e.key === "E" || e.key === ".") e.preventDefault();
                            if (e.key === "Enter") { e.preventDefault(); focusAdd(); }
                        }}
                    />
                </div>
            </div>
        );

        const removeButton = (
            <button
                type="button"
                onClick={() => removeRow(index)}
                aria-label={`Remove ${m.product || "medicine"}`}
                title="Remove"
                className="h-7 w-7 touch:h-10 touch:w-10 shrink-0 flex items-center justify-center rounded-md text-muted-foreground/70 hover:text-destructive hover:bg-destructive/10"
            >
                <X className="w-4 h-4" />
            </button>
        );

        if (stacked) {
            // Tablets / phones: the medicine on its own line, dose / when / duration / qty below it
            return (
                <div key={fieldId} className="px-3 py-2.5 space-y-2">
                    <div className="flex items-start gap-2">
                        <span className="w-5 pt-2 text-sm text-muted-foreground tabular-nums shrink-0">{n}</span>
                        <div className="flex-1 min-w-0">{medicine}</div>
                        {removeButton}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-[minmax(0,1.6fr)_minmax(0,1.2fr)_minmax(0,1fr)_64px] gap-2 pl-7">
                        {dose}{when}{duration}{quantity}
                    </div>
                </div>
            );
        }
        return (
            <div key={fieldId} className={cn(MED_GRID, "py-[7px] items-center")}>
                <span className="text-sm text-muted-foreground tabular-nums">{n}</span>
                {medicine}{dose}{when}{duration}{quantity}
                {removeButton}
            </div>
        );
    };

    // ── Calculator drawer ────────────────────────────────────────────────────────

    const handleApplyDose = (index: number, dose: AppliedDose) => {
        // Opened on an empty row and a drug picked in the calculator → fill the row first
        if (dose.drug && !getValues(`medicines.${index}.drug_id`)) handleDrugSelect(index, dose.drug);
        setValue(`medicines.${index}.dosage`, dose.dosage, { shouldDirty: true });

        // Keep the dose basis with the medicine line (editable, so the doctor decides whether it prints)
        const notes = (getValues(`medicines.${index}.notes`) ?? "").trim();
        if (!notes.includes(dose.note)) {
            setValue(`medicines.${index}.notes`, notes ? `${notes} · ${dose.note}` : dose.note, { shouldDirty: true });
        }

        if (dose.weightKg && !getValues("vitals.weight")) {
            setValue("vitals.weight", String(dose.weightKg), { shouldDirty: true });
        }

        // Quantity: bottles for liquids (needs bottle size), tablets via the existing auto-quantity
        const days = parseDurationDays(getValues(`medicines.${index}.duration`));
        if (dose.kind === "liquid" && dose.bottleMl && days > 0) {
            const bottles = Math.ceil((dose.givePerDose * dose.dosesPerDay * days) / dose.bottleMl);
            setValue(`medicines.${index}.quantity`, String(bottles), { shouldDirty: true, shouldValidate: true });
        } else {
            handleAutoQuantity(index);
        }
        setCalcRowIndex(null);
    };

    const calcRow = calcRowIndex !== null ? watched[calcRowIndex] : null;

    // Every allergy match in the list, summarised under the table as well as on its row
    const allergyAlerts = watched.flatMap((m: any) => {
        const name = (m?.product ?? "").trim();
        if (!name) return [];
        return checkAllergies(allergyTerms, { composition: m?.generic_name ?? "", product: name })
            .map(hit => ({ ...hit, medicine: name }));
    });

    return (
        <>
        <div ref={tableRef} className="rounded-md border border-field bg-card">
            {!stacked && (
                <div className={cn(MED_GRID, "h-8 items-center rounded-t-md bg-input border-b border-border text-xs font-medium text-muted-foreground")}>
                    <span>#</span><span className="px-1">Medicine</span><span className="px-1">Dose</span><span className="px-1">When</span>
                    <span className="px-1">Duration</span><span className="px-1 text-right">Qty</span><span />
                </div>
            )}
            <div className="divide-y divide-divider">
                {visibleRows.map(({ index }, i) => renderRow(index, i + 1, stacked))}
            </div>
            {/* New medicine: pick from the catalog, or Enter on a name that isn't in it */}
            <div className={cn("flex items-center gap-2 px-3 py-2", visibleRows.length > 0 && "border-t border-divider")}>
                <div className="flex-1 flex items-center h-8 touch:h-10 rounded-md border border-dashed border-field bg-card px-2.5 focus-within:border-ring focus-within:border-solid focus-within:ring-2 focus-within:ring-ring/20">
                    <div className="flex-1 min-w-0">
                    <DrugSearchInput
                        key={addKey}
                        value=""
                        onChange={addPicked}
                        onCustomInput={(val) => { addTextRef.current = val; }}
                        onEnterNext={addTyped}
                        placeholder="Type a medicine, brand or generic…"
                        className="text-sm font-normal py-0 px-0"
                        ref={addInputRef}
                        templateOptions={templateOptions}
                        onPickTemplate={(id) => { const t = rxTemplates?.templates.find(x => x.id === id); if (t) rxTemplates?.apply(t); }}
                    />
                    </div>
                </div>
                {/* Repeat the last prescription's medicines — here because the Last visit panel only fits on wide screens */}
                {repeatable.length > 0 && lastPrescription && (
                    <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => repeatLastMedicines(lastPrescription, getValues, setValue)}
                        title={`Add ${repeatable.map(m => m.product).join(", ")} from ${formatDate(lastPrescription.date)}`}
                        aria-label={`Add ${repeatable.length} medicine${repeatable.length > 1 ? "s" : ""} from the last prescription (${formatDate(lastPrescription.date)})`}
                        className="h-8 touch:h-10 shrink-0 gap-1.5 px-2.5 sm:px-3 text-[13px] font-medium"
                    >
                        <History className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Last Rx</span>
                    </Button>
                )}
                <TemplateMenu type="medicine" withLabel />
            </div>
        </div>
        {allergyAlerts.length > 0 && (
            <div className="mt-2 space-y-1.5">
                {allergyAlerts.map(a => (
                    <SafetyAlert key={`${a.medicine}|${a.text}`} level={a.level}
                        title={a.level === "error" ? `${a.medicine} — patient is allergic to ${a.allergy}` : `${a.medicine} — check against ${a.allergy} allergy`}>
                        {a.text.replace(/^Allergy: [^—]*— /, "")}. {a.level === "error" ? "Remove it, or you'll be asked to confirm on save." : "Review before saving."}
                    </SafetyAlert>
                ))}
            </div>
        )}
        {(interactions.length > 0 || interactionsFailed) && (
            <div role="alert" className="mt-2 rounded-md border border-border bg-card divide-y divide-divider">
                {interactionsFailed && (
                    <p className="px-3 py-2 text-xs text-muted-foreground">Couldn't check drug interactions — review manually.</p>
                )}
                {[...interactions]
                    .sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity])
                    .map(i => (
                        <div key={i.id} className="flex items-start gap-2 px-3 py-2 text-xs">
                            <span className={cn("shrink-0 rounded px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wide", SEVERITY_STYLE[i.severity])}>
                                {i.severity}
                            </span>
                            <div className="min-w-0">
                                <p className="font-medium text-foreground">{interactionLine(i)}</p>
                                <p className="text-muted-foreground">{i.description}{i.mechanism ? ` — ${i.mechanism}` : ""}</p>
                            </div>
                        </div>
                    ))}
            </div>
        )}
        <Sheet open={calcRowIndex !== null} onOpenChange={(open) => { if (!open) setCalcRowIndex(null); }}>
            <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto p-4">
                <SheetHeader className="mb-3">
                    <SheetTitle className="text-sm">Dose calculator</SheetTitle>
                </SheetHeader>
                {calcRowIndex !== null && (
                    <PedsDoseCalculator
                        key={`${calcRowIndex}-${calcRow?.drug_id ?? "none"}`}
                        initialDrugId={typeof calcRow?.drug_id === "number" ? calcRow.drug_id : undefined}
                        initialWeightKg={weightKg > 0 ? weightKg : undefined}
                        initialAgeMonths={ageMonths ?? undefined}
                        onApply={(dose) => handleApplyDose(calcRowIndex, dose)}
                    />
                )}
            </SheetContent>
        </Sheet>
        </>
    );
}